import React, { useEffect, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import DropZone from '../../components/DropZone';
import { CellType, FormatDate, isDate, toHalfWidth, toStringDate, toStringYYYY } from '../../modules/appLibrary';

const COLORS = {
    panelBg: 'var(--md-panel-bg, #efefef)',
    headBg: 'var(--md-head-bg, #8ea0a0)',
    headText: 'var(--md-head-fg, #fff)',
    line: '#d7d7d7',
    btn: 'var(--md-btn-bg, #4caf50)',
    btnText: '#fff',
    listEvenBg: '#f0f1fb'
};
const MIN_SIZE_COUNT = 5;
const MIN_COLOR_COUNT = 6;
const SEASON_FIELD_KEY = 'HinbanDialog_SeasonCd';
const SALES_SEASON_FIELD_KEY = 'PDT_HbSeasonCd';
const CELLTYPE = CellType();
const requiredFields = ['brand', 'year', 'season', 'salesSeason'];
const DISPLAY_CONDITION_WARNING_MESSAGE = '現在の表示条件に合わない内容へ変更されましたので展開ボードから表示されなくなります。ご注意下さい！';

const maxLengths = {
    hinbanName: 40,
    brand: 5,
    kikakuNo: 15,
    mdTanto: 10,
    category: 2,
    ecName: 40,
    patternNo: 20,
    sizePattern: 2,
    supplierProductNo: 40,
    productDescription: 255,
    shopComment: 255
};
const alphanumericFields = ['kikakuNo', 'mdTanto', 'supplierProductNo'];
const integerMaxDigits = {
    provisionalPrice: 8,
    latestPrice: 8,
    provisionalCost: 7
};
const registrationValidationFields = [
    'hinbanName',
    'brand',
    'kikakuNo',
    'mdTanto',
    'year',
    'season',
    'salesSeason',
    'category',
    'ecName',
    'patternNo',
    'sizePattern',
    'provisionalPrice',
    'latestPrice',
    'provisionalCost',
    'plannedCost',
    'supplierProductNo',
    'productDescription',
    'shopComment'
];
const registrationValidationLabels = {
    hinbanName: '品番名',
    brand: 'ブランド',
    kikakuNo: '企画No',
    mdTanto: 'MD担当者',
    year: '年度',
    season: 'シーズン',
    salesSeason: '販売シーズン',
    category: 'カテゴリ',
    ecName: 'EC表示名',
    patternNo: 'パターンNo',
    sizePattern: 'サイズパターン',
    provisionalPrice: '仮上代',
    latestPrice: '最新上代',
    provisionalCost: '仮原価',
    plannedCost: '予定原価',
    supplierProductNo: '先方品番',
    productDescription: '商品説明',
    shopComment: '店舗向けコメント'
};

const initialForm = {
    hinban: '',
    kariHinban: '',
    hinbanName: '',
    brandGroup: '',
    brand: '',
    kikakuNo: '',
    kikakuName: '',
    mdTanto: '',
    year: '',
    season: '',
    jutakuHinFlg: '',
    salesSeason: '',
    item: '',
    category: '',
    ecName: '',
    shopDate: '',
    rolloutMonth: '',
    rolloutWeek: '',
    patternNo: '',
    sizeExpansionCode: '',
    sizePattern: '',
    provisionalPrice: '',
    latestPrice: '',
    provisionalCost: '',
    plannedCost: '',
    costRate: '',
    supplierProductNo: '',
    instructionNo: '',
    supplier: '',
    processor: '',
    productDescription: '',
    shopComment: ''
};

const emptyOptions = [
    { value: '', label: '選択してください' }
];

function validateField(name, value) {
    const text = value === null || value === undefined ? '' : String(value);
    if (requiredFields.includes(name) && text.trim() === '') {
        return '必須項目です。';
    }
    if (name === 'year' && (!/^\d{4}$/.test(text) || Number(text) < 2001 || Number(text) > 2099)) {
        return '年度が不正です。（2001～2099）で指定して下さい。';
    }
    const maxLength = maxLengths[name];
    if (maxLength && text.length > maxLength) {
        return `${maxLength}文字以内で入力してください。`;
    }
    if (alphanumericFields.includes(name) && !/^[A-Za-z0-9]*$/.test(text)) {
        return '半角英数字で入力してください。';
    }

    const maxDigits = integerMaxDigits[name];
    if (maxDigits && text !== '' && (!/^\d+$/.test(text) || text.length > maxDigits)) {
        return `${maxDigits}桁以内の整数で入力してください。`;
    }
    if (name === 'plannedCost' && text !== ''
        && (!/^\d+(?:\.\d{1,4})?$/.test(text) || text.replace('.', '').length > 13)) {
        return '数字13桁・小数4桁以内で入力してください。';
    }

    return '';
}

function extractYearMonthFromControlDate(controlDate) {
    if (controlDate === null || controlDate === undefined) {
        return { year: null, month: null };
    }

    const parts = String(controlDate).trim().split('/');
    if (parts.length < 2) {
        return { year: null, month: null };
    }

    const year = Number(parts[0]);
    const month = Number(parts[1]);
    if (!Number.isInteger(year)
        || !Number.isInteger(month)
        || month < 1
        || month > 12) {
        return { year: null, month: null };
    }

    return { year, month };
}

function resolveYearFromControlDate(controlDate, season) {
    const { year, month } = extractYearMonthFromControlDate(controlDate);
    if (!Number.isInteger(year) || !Number.isInteger(month)) {
        return '';
    }

    const seasonText = season === null || season === undefined
        ? ''
        : String(season).trim();
    if (seasonText === '1') {
        return String(month <= 8 ? year : year + 1);
    }
    if (seasonText === '2') {
        return String(month <= 2 ? year - 1 : year);
    }

    return String(year);
}

function validateQuantity(value) {
    const text = value === null || value === undefined ? '' : String(value).trim();
    if (text === '') {
        return '';
    }
    if (!/^\d{1,6}$/.test(text)) {
        return '0以上の整数6桁以内で入力してください。';
    }

    return '';
}

// 品番登録エラーを見出し＋詳細へ分け、警告ダイアログで読みやすく表示する。
function buildRegistrationWarningLines(title, detail) {
    const heading = String(title || '').trim();
    const lines = heading === '' ? [] : [heading];
    const values = Array.isArray(detail) ? detail : [detail];
    const headingKey = heading.replace(/[\s。]/g, '');
    values.forEach((value) => {
        String(value === null || value === undefined ? '' : value)
            .split(/\r\n|\n/)
            .map((line) => line.trim())
            .filter((line) => line !== '')
            .forEach((line) => {
                if (line.replace(/[\s。]/g, '') === headingKey) return;
                lines.push(line);
            });
    });
    return lines;
}

function resolveMapCardValue(item, keys) {
    if (!item || !Array.isArray(keys)) {
        return '';
    }

    for (const key of keys) {
        if (!key || !Object.prototype.hasOwnProperty.call(item, key)) {
            continue;
        }

        const value = item[key];
        if (value !== null && value !== undefined && String(value).trim() !== '') {
            return value;
        }
    }

    return '';
}

function toText(value) {
    return value === null || value === undefined ? '' : String(value);
}

function buildEditInitialValues(mapCardItem) {
    const item = mapCardItem || {};

    return {
        hinban: toText(resolveMapCardValue(item, ['PDT_SYOHINCD', 'PDT_SyohinCD', 'PDT_HINBAN', 'PDT_HINBANCD'])),
        kariHinban: toText(resolveMapCardValue(item, ['PDTMD_KARIHINBAN'])),
        hinbanName: toText(resolveMapCardValue(item, ['PDT_HINMEINM', 'PDT_HinmeiNm', 'name', 'productName'])),
        brandGroup: toText(resolveMapCardValue(item, ['PDTMD_BRDGP', 'BRANDGROUP'])),
        brand: toText(resolveMapCardValue(item, ['PDT_BRDCODE', 'BRAND'])),
        kikakuNo: toText(resolveMapCardValue(item, ['PDTMD_KIKAKUNO', 'SSDX_KIKAKUNO'])),
        year: toText(resolveMapCardValue(item, ['PDT_YEAR', 'SSD_YEAR'])),
        season: toText(resolveMapCardValue(item, ['PDT_SEASONCD', 'SSD_SEASONCD'])),
        salesSeason: toText(resolveMapCardValue(item, ['PDT_HBSEASONCD', 'SSD_SEASONCD'])),
        item: toText(resolveMapCardValue(item, ['PDT_ITMITEMCD', 'PDT_ITMCITEMCD', 'PDT_AITEMCITEMCD'])),
        category: toText(resolveMapCardValue(item, ['PDTMD_CATEGORY'])),
        mdTanto: toText(resolveMapCardValue(item, ['PDT_MDTANTOCD'])),
        ecName: toText(resolveMapCardValue(item, ['PDTMD_ECNAME'])),
        shopDate: toText(resolveMapCardValue(item, ['PDTMD_SHOPYMD', 'SSDX_SHOPYMD'])),
        rolloutMonth: toText(resolveMapCardValue(item, ['PDTMD_SHOPYMDM', 'SSDX_SHOPYMDM'])),
        rolloutWeek: toText(resolveMapCardValue(item, ['PDTMD_SHOPYMDW', 'SSDX_SHOPYMDW'])),
        patternNo: toText(resolveMapCardValue(item, ['PDT_PATTERNNO1'])),
        sizeExpansionCode: toText(resolveMapCardValue(item, ['PDT_TENKAICODE'])),
        sizePattern: toText(resolveMapCardValue(item, ['PDT_SIZEPATUN'])),
        supplierProductNo: toText(resolveMapCardValue(item, ['PDT_MAKERHINBAN'])),
        supplier: toText(resolveMapCardValue(item, ['PDT_TORCODE', 'SSD_SIIRECD'])),
        processor: toText(resolveMapCardValue(item, ['PDT_KAKOUSAKICD', 'SSD_KAKOUSAKICD'])),
        provisionalPrice: toText(resolveMapCardValue(item, ['PDTMD_KARIPRICE', 'SSDX_PRICE'])),
        latestPrice: toText(resolveMapCardValue(item, ['PDT_PRICE', 'SSDX_PRICE'])),
        provisionalCost: toText(resolveMapCardValue(item, ['PDT_KARIGENKA'])),
        plannedCost: toText(resolveMapCardValue(item, ['PDTMD_YOTEIGENKA'])),
        productDescription: toText(resolveMapCardValue(item, ['PDT_SHOHINSETSUMEI1'])),
        shopComment: toText(resolveMapCardValue(item, ['PDT_TEKIYO'])),
        instructionNo: toText(resolveMapCardValue(item, ['SSD_NO']))
    };
}

function withoutEmptyInitialValues(values) {
    return Object.entries(values || {}).reduce((result, [key, value]) => {
        if (value !== null && value !== undefined && String(value).trim() !== '') {
            result[key] = value;
        }
        return result;
    }, {});
}

function buildEditDetailState(mapCardItem) {
    const details = Array.isArray(mapCardItem?.TENKAIMD_EDIT_DETAILS)
        ? mapCardItem.TENKAIMD_EDIT_DETAILS
        : [];
    const colors = [];
    const sizes = [];
    const colorSet = new Set();
    const sizeSet = new Set();
    const quantityMap = new Map();

    details.forEach((detail) => {
        const colorCode = toText(resolveMapCardValue(detail, ['ColorCode', 'COLORCODE', 'SSDM_COLORCD'])).trim();
        const sizeCode = toText(resolveMapCardValue(detail, ['SizeCode', 'SIZECODE', 'SSDM_SIZECD'])).trim();
        if (colorCode === '' || sizeCode === '') return;

        if (!colorSet.has(colorCode)) {
            colorSet.add(colorCode);
            colors.push(colorCode);
        }
        if (!sizeSet.has(sizeCode)) {
            sizeSet.add(sizeCode);
            sizes.push(sizeCode);
        }
        const quantity = resolveMapCardValue(detail, ['Quantity', 'QUANTITY', 'SSDM_MDHATCHUSU']);
        quantityMap.set(JSON.stringify([colorCode, sizeCode]), toText(quantity));
    });

    return {
        colors,
        sizes,
        quantities: colors.map((colorCode) => sizes.map((sizeCode) => (
            quantityMap.get(JSON.stringify([colorCode, sizeCode])) || ''
        )))
    };
}

function buildEditKeyValues(mapCardItem) {
    const item = mapCardItem || {};

    return {
        oppiCd: toHalfWidth(resolveMapCardValue(item, ['PDT_OPPICD', 'SSD_OPPICD']) || '').trim(),
        ssdNo: toHalfWidth(resolveMapCardValue(item, ['SSD_NO']) || '').trim(),
        ssdmNo: toHalfWidth(resolveMapCardValue(item, ['SSDM_NO', 'SSD_NO']) || '').trim(),
        ssdxNo: toHalfWidth(resolveMapCardValue(item, ['SSDX_NO', 'SSD_NO']) || '').trim(),
        year: toHalfWidth(resolveMapCardValue(item, ['PDT_YEAR', 'SSD_YEAR']) || '').trim(),
        seasonCode: toHalfWidth(resolveMapCardValue(item, ['PDT_SEASONCD', 'SSD_SEASONCD']) || '').trim(),
        salesSeasonCode: toHalfWidth(resolveMapCardValue(item, ['PDT_HBSEASONCD', 'SSD_SEASONCD']) || '').trim(),
        brandCode: toHalfWidth(resolveMapCardValue(item, ['PDT_BRDCODE', 'BRAND']) || '').trim()
    };
}

function sumQuantityValues(values) {
    let total = 0;
    let hasValue = false;

    values.forEach((value) => {
        const text = value === null || value === undefined ? '' : String(value).trim();
        if (text === '') return;

        const number = Number(text);
        if (!Number.isFinite(number)) return;

        total += number;
        hasValue = true;
    });

    return hasValue ? String(total) : '';
}

function normalizeSupplierProductNo(value) {
    return toHalfWidth(value === null || value === undefined ? '' : String(value))
        .replace(/[^A-Za-z0-9]/g, '');
}

function createShopDateValues(value) {
    const shopDate = value === '' || value === null || value === undefined
        ? ''
        : isDate(value) ? FormatDate(value) : '';
    if (shopDate === '') {
        return { shopDate: '', rolloutMonth: '', rolloutWeek: '' };
    }

    const [year, month, day] = shopDate.split('/').map(Number);
    const isoDate = new Date(Date.UTC(year, month - 1, day));
    const dayOfWeek = isoDate.getUTCDay() || 7;
    isoDate.setUTCDate(isoDate.getUTCDate() + 4 - dayOfWeek);
    const isoYearStart = new Date(Date.UTC(isoDate.getUTCFullYear(), 0, 1));
    const isoWeek = Math.ceil((((isoDate - isoYearStart) / 86400000) + 1) / 7);

    return {
        shopDate,
        rolloutMonth: `${month}月`,
        rolloutWeek: `${isoWeek}W`
    };
}

function rebuildQuantities(currentColors, currentSizes, currentQuantities, nextColors, nextSizes) {
    const quantityMap = new Map();

    currentColors.forEach((colorCode, colorIndex) => {
        currentSizes.forEach((sizeCode, sizeIndex) => {
            const quantity = currentQuantities[colorIndex]?.[sizeIndex];
            if (quantity === undefined) return;

            quantityMap.set(JSON.stringify([colorCode, sizeCode]), quantity);
        });
    });

    return nextColors.map((colorCode) => nextSizes.map((sizeCode) => {
        const key = JSON.stringify([colorCode, sizeCode]);
        return quantityMap.has(key) ? quantityMap.get(key) : '';
    }));
}

function FormField({ name, label, value, onChange, onBlur, onSearch, error, required, select, readOnly, multiline, rows, options, inputMode, autoCapitalize, spellCheck, type = 'text' }) {
    const optionList = Array.isArray(options) ? options : [];
    const hasSelectedOption = optionList.some((option) => String(option.code) === String(value));
    const inputProps = {
        style: {
            height: multiline ? undefined : 28,
            fontSize: 12
        },
        ...(readOnly ? { readOnly: true } : {}),
        ...(onSearch ? {
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton size="small" onClick={onSearch}>
                        <Icon>arrow_right</Icon>
                    </IconButton>
                </InputAdornment>
            )
        } : {})
    };

    return (
        <Box>
            <Box mb={0.1} display="flex" alignItems="center">
                <Typography variant="caption">{label}</Typography>
                {required ? (
                    <Typography variant="caption" color="error" style={{ marginLeft: 4 }}>*</Typography>
                ) : null}
                {error ? (
                    <Tooltip title={error}>
                        <div style={{ marginLeft: 4, height: 18, cursor: 'pointer' }}>
                            <span className="material-icons warning" style={{ fontSize: 18 }}>
                                announcement
                            </span>
                        </div>
                    </Tooltip>
                ) : null}
            </Box>
            <TextField
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                required={required}
                select={select}
                type={type}
                multiline={multiline}
                rows={rows}
                onDoubleClick={onSearch}
                variant="outlined"
                size="small"
                fullWidth={true}
                InputProps={inputProps}
                inputProps={{ inputMode, autoCapitalize, spellCheck, style: { fontSize: 12 } }}
            >
                {select ? emptyOptions.map((option) => (
                    <MenuItem key={option.value || 'empty'} value={option.value}>
                        {option.label}
                    </MenuItem>
                )) : null}
                {select && Array.isArray(options) && value !== '' && !hasSelectedOption ? (
                    <MenuItem value={value}>{value}</MenuItem>
                ) : null}
                {select ? optionList.map((option) => (
                    <MenuItem key={option.rowid || option.code} value={option.code}>
                        {option.name}
                    </MenuItem>
                )) : null}
            </TextField>
        </Box>
    );
}

export default function TenkaiMdHinbanDialog(props) {
    const editMode = props.editMode === true;
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [seasonOptions, setSeasonOptions] = useState([]);
    const [salesSeasonOptions, setSalesSeasonOptions] = useState([]);
    const [sizeExpansionOptions, setSizeExpansionOptions] = useState([]);
    const [colors, setColors] = useState([]);
    const [sizes, setSizes] = useState([]);
    const [quantities, setQuantities] = useState([]);
    const [quantityErrors, setQuantityErrors] = useState([]);
    const [isImageDragActive, setIsImageDragActive] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const selectedImageUrlRef = useRef(null);
    const controlDateRef = useRef('');
    const displayedSizes = Array.from(
        { length: Math.max(sizes.length, MIN_SIZE_COUNT) },
        (_, index) => sizes[index] || ''
    );
    const displayedColors = Array.from(
        { length: Math.max(colors.length, MIN_COLOR_COUNT) },
        (_, index) => colors[index] || ''
    );
    const rowTotals = displayedColors.map((color, colorIndex) => (
        String(color || '').trim() === ''
            ? ''
            : sumQuantityValues(sizes.map((size, sizeIndex) => (
                String(size || '').trim() === ''
                    ? ''
                    : quantities[colorIndex] && quantities[colorIndex][sizeIndex]
            )))
    ));
    const columnTotals = displayedSizes.map((size, sizeIndex) => (
        String(size || '').trim() === ''
            ? ''
            : sumQuantityValues(colors.map((color, colorIndex) => (
                String(color || '').trim() === ''
                    ? ''
                    : quantities[colorIndex] && quantities[colorIndex][sizeIndex]
            )))
    ));
    const grandTotal = sumQuantityValues(colors.reduce((values, color, colorIndex) => {
        if (String(color || '').trim() === '') return values;

        sizes.forEach((size, sizeIndex) => {
            if (String(size || '').trim() === '') return;
            values.push(quantities[colorIndex] && quantities[colorIndex][sizeIndex]);
        });
        return values;
    }, []));
    const matrixMinWidth = 140 + displayedSizes.length * 110 + 90;

    useEffect(() => {
        if (selectedImageUrlRef.current) {
            URL.revokeObjectURL(selectedImageUrlRef.current);
            selectedImageUrlRef.current = null;
        }
        setSelectedImage(null);
        setIsImageDragActive(false);
    }, [props.open]);

    useEffect(() => () => {
        if (selectedImageUrlRef.current) {
            URL.revokeObjectURL(selectedImageUrlRef.current);
        }
    }, []);

    useEffect(() => {
        if (!props.open) return;
        const editDetails = editMode
            ? buildEditDetailState(props.mapCardItem)
            : { colors: [], sizes: [], quantities: [] };
        const editInitialValues = editMode
            ? withoutEmptyInitialValues(buildEditInitialValues(props.mapCardItem))
            : {};
        setForm({
            ...initialForm,
            ...(props.initialValues || {}),
            ...editInitialValues
        });
        setErrors({});
        setColors(editDetails.colors);
        setSizes(editDetails.sizes);
        setQuantities(editDetails.quantities);
        setQuantityErrors([]);
    }, [editMode, props.mapCardItem, props.open, props.initialValues]);

    useEffect(() => {
        controlDateRef.current = '';
        if (!props.open) return;

        let active = true;
        const client = props.fnc && props.fnc.current;
        if (!client || !client.postaxios) return;

        function warnControlDate(data) {
            if (!client.warnmsg) return;
            client.warnmsg((data && data.errDescription)
                ? data.errDescription
                : 'ControlDateの取得に失敗しました');
        }

        client.postaxios(
            'hinbanmaster/GetControlDate',
            null,
            {
                onSuccess: function (data) {
                    if (!active) return;

                    const controlDate = data && data.request;
                    const { year, month } = extractYearMonthFromControlDate(controlDate);
                    if (!Number.isInteger(year) || !Number.isInteger(month)) {
                        warnControlDate(data);
                        return;
                    }

                    controlDateRef.current = String(controlDate);
                    setForm((current) => {
                        const calculatedYear = resolveYearFromControlDate(controlDate, current.season);
                        if (editMode) {
                            return current;
                        }
                        setErrors((currentErrors) => ({
                            ...currentErrors,
                            year: validateField('year', calculatedYear)
                        }));
                        return {
                            ...current,
                            year: calculatedYear
                        };
                    });
                },
                onError: function (data) {
                    if (!active) return;
                    warnControlDate(data);
                }
            },
            true
        );

        return () => {
            active = false;
            controlDateRef.current = '';
        };
    }, [editMode, props.open, props.fnc]);

    useEffect(() => {
        if (!props.open) return;

        setSeasonOptions([]);
        setSalesSeasonOptions([]);

        const client = props.fnc && props.fnc.current;
        if (!client || !client.postaxios) return;

        function loadOptions(fieldKey, setOptions) {
            client.postaxios("mdtounyu/GetInputMasterOptions", {
                type: fieldKey,
                rowdata: [{}]
            }, {
                onSuccess: function (data) {
                    const options = Array.isArray(data && data.rowdata)
                        ? data.rowdata
                            .map((row) => ({
                                rowid: row && row.rowid !== null && row.rowid !== undefined ? String(row.rowid).trim() : '',
                                code: row && row.code !== null && row.code !== undefined ? String(row.code).trim() : '',
                                name: row && row.name !== null && row.name !== undefined ? String(row.name).trim() : ''
                            }))
                            .filter((option) => option.code !== '')
                        : [];

                    if (client.wait) client.wait(false);
                    setOptions(options);
                }
            });
        }

        loadOptions(SEASON_FIELD_KEY, setSeasonOptions);
        loadOptions(SALES_SEASON_FIELD_KEY, setSalesSeasonOptions);
    }, [props.open, props.fnc]);

    useEffect(() => {
        let active = true;
        const brand = String(form.brand || '').trim();
        const item = String(form.item || '').trim();

        setSizeExpansionOptions([]);
        if (!props.open || brand === '' || item === '') {
            return () => { active = false; };
        }

        const client = props.fnc && props.fnc.current;
        if (!client || !client.postaxios) {
            return () => { active = false; };
        }

        client.postaxios("hinbanmaster/GetPdtTenkaiCodes", {
            PDT_BRDCODE: brand,
            PDT_ITMCITEMCD: item
        }, {
            onSuccess: function (data) {
                if (!active) return;

                const returnData = data && data.returnData ? data.returnData : {};
                const count = Number(returnData.Count) || 0;
                const used = new Set();
                const options = [];

                for (let index = 1; index <= count; index++) {
                    const code = String(returnData[`PDTL_TENKAICODE${index}`] || '').trim();
                    if (code === '' || used.has(code)) continue;
                    used.add(code);
                    options.push({ code, name: code });
                }

                setSizeExpansionOptions(options);
            },
            onError: function () {
                if (active) setSizeExpansionOptions([]);
            }
        }, true);

        return () => { active = false; };
    }, [props.open, props.fnc, form.brand, form.item]);

    useEffect(() => {
        let active = true;
        const brand = String(form.brand || '').trim();
        const item = String(form.item || '').trim();
        const sizeExpansionCode = String(form.sizeExpansionCode || '').trim();

        if (!props.open || brand === '' || item === '' || sizeExpansionCode === '') {
            return () => { active = false; };
        }

        const client = props.fnc && props.fnc.current;
        if (!client || !client.postaxios) {
            return () => { active = false; };
        }

        client.postaxios("hinbanmaster/GetPdtTenkaiSizePatternInfo", {
            PDT_BRDCODE: brand,
            PDT_ITMCITEMCD: item,
            PDT_TENKAICODE: sizeExpansionCode
        }, {
            onSuccess: function (data) {
                if (!active) return;

                const returnData = data && data.returnData ? data.returnData : {};
                const found = returnData.Found === true
                    || String(returnData.Found || '').toLowerCase() === 'true';
                if (!found) return;

                const sizePattern = String(returnData.SZPI_SZPCODE || '').trim();
                setForm((current) => ({ ...current, sizePattern }));
            },
            onError: function () {
                if (!active) return;
            }
        }, true);

        return () => { active = false; };
    }, [props.open, props.fnc, form.brand, form.item, form.sizeExpansionCode]);

    function handleFormChange(event) {
        const { name, value } = event.target;

        if (name === 'season') {
            const calculatedYear = resolveYearFromControlDate(
                controlDateRef.current,
                value
            );

            setForm((current) => ({
                ...current,
                season: value,
                ...(calculatedYear === '' ? {} : { year: calculatedYear })
            }));

            if (calculatedYear !== '') {
                setErrors((current) => ({
                    ...current,
                    year: validateField('year', calculatedYear)
                }));
            }
            return;
        }

        if (name === 'supplierProductNo') {
            setForm((current) => ({
                ...current,
                supplierProductNo: normalizeSupplierProductNo(value)
            }));
            return;
        }
        if (name === 'brand') {
            if (value === form.brand) return;

            setSizeExpansionOptions([]);
            setColors([]);
            setSizes([]);
            setQuantities([]);
            setQuantityErrors([]);
            setForm((current) => ({
                ...current,
                brand: value,
                item: '',
                sizeExpansionCode: '',
                sizePattern: '',
                processor: ''
            }));
            return;
        }
        if (name === 'item') {
            if (value === form.item) return;

            setSizeExpansionOptions([]);
            setSizes([]);
            setQuantities([]);
            setQuantityErrors([]);
            setForm((current) => ({
                ...current,
                item: value,
                sizeExpansionCode: '',
                sizePattern: ''
            }));
            return;
        }
        if (name === 'sizeExpansionCode') {
            if (value === form.sizeExpansionCode) return;

            setSizes([]);
            setQuantities([]);
            setQuantityErrors([]);
            setForm((current) => ({
                ...current,
                sizeExpansionCode: value,
                sizePattern: ''
            }));
            return;
        }

        setForm((current) => ({ ...current, [name]: value }));
    }

    function handleFormBlur(event) {
        const { name, value } = event.target;

        if (name === 'shopDate') {
            const shopDateValues = createShopDateValues(value);
            setForm((current) => ({ ...current, ...shopDateValues }));
            return;
        }

        const message = validateField(name, value);

        setErrors((current) => ({
            ...current,
            [name]: message
        }));
    }

    function handleDialogClose(event, reason) {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        if (props.onClose) props.onClose();
    }

    function handleImageDragEnter(event) {
        if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
            setIsImageDragActive(true);
        }
    }

    function handleImageDropClose() {
        setIsImageDragActive(false);
    }

    function handleImageUploaded(data) {
        const images = data && Array.isArray(data.images) ? data.images : [];
        if (images.length === 0) return;

        if (selectedImageUrlRef.current) {
            URL.revokeObjectURL(selectedImageUrlRef.current);
        }
        selectedImageUrlRef.current = images[0].imageurl;
        setSelectedImage(images[0]);

        images.slice(1).forEach((image) => {
            if (image.imageurl) URL.revokeObjectURL(image.imageurl);
        });
    }

    function handleBrandSearchOpen() {
        const client = props.fnc && props.fnc.current;
        if (!client || !client.searchlistopen) return;

        const currentBrand = String(form.brand || '').trim();
        client.searchlistopen({
            label: 'ブランド',
            multiple: false,
            rowmode: false,
            data: {
                dataid: 'M_BRD:BASE',
                columnid: 'PDT_BRDCODE',
                columnnm: 'ブランド',
                datatype: CELLTYPE.MASTERCODE,
                initial: true,
                initialSelectedCodes: currentBrand === '' ? [] : [currentBrand]
            },
            rowdata: {
                PDT_BRDCODE: currentBrand
            },
            doApply: function (eventData) {
                const selectedBrand = eventData && eventData.value !== null && eventData.value !== undefined
                    ? String(eventData.value).trim()
                    : '';
                if (selectedBrand === '') return;

                if (selectedBrand !== currentBrand) {
                    setSizeExpansionOptions([]);
                    setColors([]);
                    setSizes([]);
                    setQuantities([]);
                    setQuantityErrors([]);
                    setForm((current) => ({
                        ...current,
                        brand: selectedBrand,
                        item: '',
                        sizeExpansionCode: '',
                        sizePattern: '',
                        processor: ''
                    }));
                }
                setErrors((current) => ({ ...current, brand: '' }));
                if (client.searchlistclose) client.searchlistclose();
            }
        });
    }

    function handleSupplierSearchOpen() {
        const client = props.fnc && props.fnc.current;
        if (!client || !client.searchlistopen) return;

        const currentSupplier = String(form.supplier || '').trim();
        client.searchlistopen({
            label: '仕入先',
            multiple: false,
            rowmode: false,
            data: {
                dataid: 'V_SUPPLIER:MDTOUNYU',
                columnid: 'supplier',
                columnnm: '仕入先',
                datatype: CELLTYPE.MASTERCODE,
                initial: true,
                initialSelectedCodes: currentSupplier === '' ? [] : [currentSupplier]
            },
            rowdata: {
                TNY_JUTAKUHINFLG: form.jutakuHinFlg
            },
            doApply: function (eventData) {
                const selectedSupplier = eventData && eventData.value !== null && eventData.value !== undefined
                    ? String(eventData.value).trim()
                    : '';
                if (selectedSupplier === '') return;

                if (selectedSupplier !== currentSupplier) {
                    setForm((current) => ({
                        ...current,
                        supplier: selectedSupplier,
                        processor: ''
                    }));
                }
                if (client.searchlistclose) client.searchlistclose();
            }
        });
    }

    function handleProcessorSearchOpen() {
        const client = props.fnc && props.fnc.current;
        if (!client || !client.searchlistopen) return;

        const brand = String(form.brand || '').trim();
        if (brand === '') {
            if (client.warnmsg) client.warnmsg('先にブランドを選択してください。');
            return;
        }

        const supplier = String(form.supplier || '').trim();
        if (supplier === '') {
            if (client.warnmsg) client.warnmsg('先に仕入先を選択してください。');
            return;
        }

        const currentProcessor = String(form.processor || '').trim();
        client.searchlistopen({
            label: '加工先',
            multiple: false,
            rowmode: false,
            data: {
                dataid: 'V_FACTORY:MDTOUNYU',
                columnid: 'processor',
                columnnm: '加工先',
                datatype: CELLTYPE.MASTERCODE,
                initial: true,
                initialSelectedCodes: currentProcessor === '' ? [] : [currentProcessor]
            },
            rowdata: {
                PDT_BRDCODE: brand,
                TNYM_SIIRESAKICD: supplier,
                PDT_SHOHINKBN: '01'
            },
            doApply: function (eventData) {
                const selectedValue = eventData && eventData.value !== null && eventData.value !== undefined
                    ? String(eventData.value).trim()
                    : '';
                const selectedEntry = eventData && Array.isArray(eventData.items) && eventData.items.length !== 0
                    ? eventData.items[0]
                    : null;
                const selectedRow = selectedEntry && selectedEntry.data && typeof selectedEntry.data === 'object'
                    ? selectedEntry.data
                    : selectedEntry;
                const selectedProcessor = selectedValue !== ''
                    ? selectedValue
                    : String((selectedRow && selectedRow.code) || '').trim();
                if (selectedProcessor === '') return;

                setForm((current) => ({ ...current, processor: selectedProcessor }));
                if (client.searchlistclose) client.searchlistclose();
            }
        });
    }

    function handleItemSearchOpen() {
        const client = props.fnc && props.fnc.current;
        if (!client || !client.postaxios || !client.searchlistopen) return;

        const brand = String(form.brand || '').trim();
        if (brand === '') {
            if (client.warnmsg) client.warnmsg('先にブランドを選択してください。');
            return;
        }

        const currentItem = String(form.item || '').trim();
        client.postaxios("mdtounyu/GetInputMasterOptions", {
            type: 'TNYM_ITMCd',
            rowdata: [{
                PDT_BrdCode: brand
            }]
        }, {
            onSuccess: function (data) {
                const options = Array.isArray(data && data.rowdata)
                    ? data.rowdata
                        .map((row) => ({
                            rowid: String((row && (row.rowid || row.code)) || '').trim(),
                            code: String((row && row.code) || '').trim(),
                            name: String((row && row.name) || '').trim()
                        }))
                        .filter((option) => option.code !== '')
                    : [];

                if (client.wait) client.wait(false);
                if (options.length === 0) {
                    if (client.warnmsg) client.warnmsg('アイテムの検索候補がありません。');
                    return;
                }

                client.searchlistopen({
                    label: 'アイテム',
                    multiple: false,
                    rowmode: false,
                    data: {
                        dataid: '',
                        columnid: 'item',
                        columnnm: 'アイテム',
                        datatype: String(CELLTYPE.SELECT),
                        options
                    },
                    rowdata: {
                        item: currentItem
                    },
                    doApply: function (eventData) {
                        const selectedItem = eventData && eventData.value !== null && eventData.value !== undefined
                            ? String(eventData.value).trim()
                            : '';
                        const selectedEntry = eventData && Array.isArray(eventData.items) && eventData.items.length !== 0
                            ? eventData.items[0]
                            : null;
                        const selectedRow = selectedEntry && selectedEntry.data && typeof selectedEntry.data === 'object'
                            ? selectedEntry.data
                            : selectedEntry;
                        const item = selectedItem !== ''
                            ? selectedItem
                            : String((selectedRow && selectedRow.code) || '').trim();

                        if (item !== '' && item !== currentItem) {
                            setSizeExpansionOptions([]);
                            setSizes([]);
                            setQuantities([]);
                            setQuantityErrors([]);
                            setForm((current) => ({
                                ...current,
                                item,
                                sizeExpansionCode: '',
                                sizePattern: ''
                            }));
                        }
                        if (client.searchlistclose) client.searchlistclose();
                        if (client.wait) client.wait(false);
                    }
                });
            },
            onError: function (data) {
                if (client.wait) client.wait(false);
                if (client.warnmsg) {
                    const message = data && data.errDescription
                        ? data.errDescription
                        : 'アイテムの検索候補取得に失敗しました。';
                    client.warnmsg(message);
                }
            }
        });
    }

    function handleYearSearchOpen() {
        const client = props.fnc && props.fnc.current;
        if (!client || !client.searchlistopen) return;

        client.searchlistopen({
            label: '年度',
            multiple: false,
            rowmode: false,
            data: {
                dataid: '',
                columnid: 'year',
                columnnm: '年度',
                datatype: CELLTYPE.YEAR,
                minYear: 2001
            },
            rowdata: {
                year: form.year
            },
            doApply: function (eventData) {
                const value = eventData ? eventData.value : null;
                if (value instanceof Date && !Number.isNaN(value.getTime())) {
                    const selectedYear = String(toStringYYYY(value));
                    setForm((current) => ({ ...current, year: selectedYear }));
                    setErrors((current) => ({ ...current, year: validateField('year', selectedYear) }));
                }
                if (client.searchlistclose) client.searchlistclose();
            }
        });
    }

    function handleShopDateSearchOpen() {
        const client = props.fnc && props.fnc.current;
        if (!client || !client.searchlistopen) return;

        client.searchlistopen({
            label: '店頭展開日',
            multiple: false,
            rowmode: false,
            data: {
                dataid: '',
                columnid: 'shopDate',
                columnnm: '店頭展開日',
                datatype: CELLTYPE.DATE
            },
            rowdata: {
                shopDate: form.shopDate
            },
            doApply: function (eventData) {
                const value = eventData ? eventData.value : null;
                if (value && typeof value.getFullYear === 'function') {
                    const shopDateValues = createShopDateValues(toStringDate(value));
                    setForm((current) => ({ ...current, ...shopDateValues }));
                }
                if (client.searchlistclose) client.searchlistclose();
            }
        });
    }

    function handleColorDoubleClick() {
        const client = props.fnc && props.fnc.current;
        if (!client || !client.searchlistopen) return;

        const selectedColors = [...new Set(colors
            .map((color) => color === null || color === undefined ? '' : String(color).trim())
            .filter((color) => color !== ''))];

        client.searchlistopen({
            label: 'カラー',
            multiple: true,
            rowmode: true,
            data: {
                dataid: 'FM_BCOL:BRAND=SHD_SHOP',
                datatype: CELLTYPE.MASTERCODE,
                initial: true,
                initialSelectedCodes: selectedColors
            },
            rowdata: {
                SHD_SHOP: form.brand
            },
            rowid: null,
            currowid: null,
            doApply: function (eventData) {
                const items = eventData && Array.isArray(eventData.items) ? eventData.items : [];
                const nextColors = [];
                const addedCodes = new Set();

                items.forEach((item) => {
                    const row = item && item.data && typeof item.data === 'object' ? item.data : null;
                    const code = row && row.code !== null && row.code !== undefined
                        ? String(row.code).trim()
                        : '';
                    if (code === '' || addedCodes.has(code)) return;

                    addedCodes.add(code);
                    nextColors.push(code);
                });

                const nextQuantities = rebuildQuantities(colors, sizes, quantities, nextColors, sizes);
                const nextQuantityErrors = rebuildQuantities(colors, sizes, quantityErrors, nextColors, sizes);
                setColors(nextColors);
                setQuantities(nextQuantities);
                setQuantityErrors(nextQuantityErrors);
                if (client.searchlistclose) client.searchlistclose();
            }
        });
    }

    function handleSizeDoubleClick() {
        const client = props.fnc && props.fnc.current;
        if (!client || !client.searchlistopen) return;

        if (!form.brand) {
            if (client.warnmsg) client.warnmsg('先にブランドを選択してください。');
            return;
        }
        if (!form.item) {
            if (client.warnmsg) client.warnmsg('先にアイテムを選択してください。');
            return;
        }
        if (!form.sizeExpansionCode) {
            if (client.warnmsg) client.warnmsg('先にサイズ展開コードを選択してください。');
            return;
        }
        if (!form.sizePattern) {
            if (client.warnmsg) client.warnmsg('先にサイズパターンを設定してください。');
            return;
        }

        const selectedSizes = [...new Set(sizes
            .map((size) => size === null || size === undefined ? '' : String(size).trim())
            .filter((size) => size !== ''))];

        client.searchlistopen({
            label: 'サイズ',
            multiple: true,
            rowmode: true,
            data: {
                dataid: 'M_SZE:MDTOUNYU',
                datatype: CELLTYPE.MASTERCODE,
                initial: true,
                initialSelectedCodes: selectedSizes
            },
            rowdata: {
                PDT_BRDCODE: form.brand,
                PDT_ITMCITEMCD: form.item,
                PDT_TENKAICODE: form.sizeExpansionCode,
                PDT_SIZEPATUN: form.sizePattern
            },
            rowid: null,
            currowid: null,
            doApply: function (eventData) {
                const items = eventData && Array.isArray(eventData.items) ? eventData.items : [];
                const nextSizes = [];
                const addedCodes = new Set();

                items.forEach((item) => {
                    const row = item && item.data && typeof item.data === 'object' ? item.data : null;
                    const code = row && row.code !== null && row.code !== undefined
                        ? String(row.code).trim()
                        : '';
                    if (code === '' || addedCodes.has(code)) return;

                    addedCodes.add(code);
                    nextSizes.push(code);
                });

                const nextQuantities = rebuildQuantities(colors, sizes, quantities, colors, nextSizes);
                const nextQuantityErrors = rebuildQuantities(colors, sizes, quantityErrors, colors, nextSizes);
                setSizes(nextSizes);
                setQuantities(nextQuantities);
                setQuantityErrors(nextQuantityErrors);
                if (client.searchlistclose) client.searchlistclose();
            }
        });
    }

    function handleQuantityChange(colorIndex, sizeIndex, value) {
        setQuantities((current) => {
            const next = current.map((row) => Array.isArray(row) ? [...row] : []);

            while (next.length <= colorIndex) {
                next.push([]);
            }
            while (next[colorIndex].length <= sizeIndex) {
                next[colorIndex].push('');
            }
            next[colorIndex][sizeIndex] = value;

            return next;
        });
        setQuantityError(colorIndex, sizeIndex, '');
    }

    function setQuantityError(colorIndex, sizeIndex, message) {
        setQuantityErrors((current) => {
            const currentMessage = current[colorIndex] && current[colorIndex][sizeIndex]
                ? current[colorIndex][sizeIndex]
                : '';
            if (currentMessage === message) return current;

            const next = current.map((row) => Array.isArray(row) ? [...row] : []);
            while (next.length <= colorIndex) {
                next.push([]);
            }
            while (next[colorIndex].length <= sizeIndex) {
                next[colorIndex].push('');
            }
            next[colorIndex][sizeIndex] = message;

            return next;
        });
    }

    function handleQuantityBlur(colorIndex, sizeIndex, value) {
        setQuantityError(colorIndex, sizeIndex, validateQuantity(value));
    }

    function createRegisterPayload() {
        const details = [];

        colors.forEach((colorCode, colorIndex) => {
            sizes.forEach((sizeCode, sizeIndex) => {
                if (String(colorCode || '').trim() === '') return;
                if (String(sizeCode || '').trim() === '') return;

                const rawQuantity = quantities[colorIndex]
                    && quantities[colorIndex][sizeIndex] !== undefined
                    ? quantities[colorIndex][sizeIndex]
                    : '';

                details.push({
                    colorCode,
                    sizeCode,
                    quantity: rawQuantity === '' ? 0 : Number(rawQuantity)
                });
            });
        });

        return {
            data: {
                mdTounyuNo: props.mdTounyuNo,
                keys: editMode ? buildEditKeyValues(props.mapCardItem) : undefined,
                product: {
                    name: form.hinbanName,
                    brandGroup: form.brandGroup,
                    year: form.year,
                    seasonCode: form.season,
                    salesSeasonCode: form.salesSeason,
                    brandCode: form.brand,
                    itemCode: form.item,
                    category: form.category,
                    sizeExpansionCode: form.sizeExpansionCode,
                    sizePatternCode: form.sizePattern,
                    planningNo: form.kikakuNo,
                    mdStaffCode: form.mdTanto,
                    ecName: form.ecName,
                    shopDate: form.shopDate,
                    rolloutMonth: form.rolloutMonth,
                    rolloutWeek: form.rolloutWeek,
                    patternNo: form.patternNo,
                    supplierProductNo: form.supplierProductNo,
                    supplierCode: form.supplier,
                    processorCode: form.processor,
                    provisionalPrice: form.provisionalPrice,
                    latestPrice: form.latestPrice,
                    provisionalCost: form.provisionalCost,
                    plannedCost: form.plannedCost,
                    description: form.productDescription,
                    shopComment: form.shopComment
                },
                details
            },
            image: selectedImage
                ? {
                    originalFileName: selectedImage.filename,
                    file: selectedImage.fileinfo
                }
                : null
        };
    }

    function handleRegister() {
        const nextErrors = {};
        registrationValidationFields.forEach((name) => {
            nextErrors[name] = validateField(name, form[name]);
        });
        setErrors(nextErrors);

        const client = props.fnc && props.fnc.current;
        if (Object.values(nextErrors).some((message) => message !== '')) {
            if (client && client.warnmsg) {
                client.warnmsg([
                    '入力内容にエラーがあります。',
                    ...registrationValidationFields
                        .filter((name) => nextErrors[name] !== '')
                        .map((name) => `${registrationValidationLabels[name] || name}：${nextErrors[name]}`)
                ]);
            }
            return;
        }

        const hasSelectedColor = colors.some((color) => String(color || '').trim() !== '');
        if (!hasSelectedColor) {
            if (client && client.warnmsg) client.warnmsg(['入力内容にエラーがあります。', 'カラーを1件以上選択してください。']);
            return;
        }

        const hasSelectedSize = sizes.some((size) => String(size || '').trim() !== '');
        if (!hasSelectedSize) {
            if (client && client.warnmsg) client.warnmsg(['入力内容にエラーがあります。', 'サイズを1件以上選択してください。']);
            return;
        }

        const nextQuantityErrors = quantities.map((row) => Array.isArray(row)
            ? row.map((quantity) => validateQuantity(quantity))
            : []);
        setQuantityErrors(nextQuantityErrors);
        if (nextQuantityErrors.some((row) => row.some((message) => message !== ''))) {
            if (client && client.warnmsg) {
                const quantityMessages = [];
                nextQuantityErrors.forEach((row, colorIndex) => {
                    row.forEach((message, sizeIndex) => {
                        if (message === '') return;
                        const colorLabel = String(colors[colorIndex] || colorIndex + 1);
                        const sizeLabel = String(sizes[sizeIndex] || sizeIndex + 1);
                        quantityMessages.push(`カラー ${colorLabel}／サイズ ${sizeLabel}：${message}`);
                    });
                });
                client.warnmsg(['数量に入力エラーがあります。', ...quantityMessages]);
            }
            return;
        }

        const payload = createRegisterPayload();
        validateDisplayConditions(payload);

        function validateDisplayConditions(registerPayload) {
            if (!client || typeof client.postaxios !== 'function') {
                if (client && client.warnmsg) {
                    client.warnmsg('表示条件を確認できませんでした。');
                }
                return;
            }

            client.wait(true);
            client.postaxios(
                'tenkaimd/ValidateHinbanDisplayConditions',
                {
                    conditions: props.displayConditions || {},
                    product: registerPayload.data.product
                },
                {
                    onSuccess: function (data) {
                        client.wait(false);
                        if (data && data.status === false) {
                            client.warnmsg(data.errDescription || data.message || '表示条件の確認に失敗しました。');
                            return;
                        }

                        if (data && data.matches === false) {
                            client.confmsg(
                                DISPLAY_CONDITION_WARNING_MESSAGE,
                                () => continueRegister(registerPayload, true)
                            );
                            return;
                        }

                        continueRegister(registerPayload, false);
                    },
                    onError: function (data) {
                        client.wait(false);
                        client.warnmsg(
                            (data && (data.errDescription || data.message))
                            || '表示条件の確認に失敗しました。'
                        );
                    },
                    errmsg: '表示条件の確認に失敗しました。'
                }
            );
        }

        function continueRegister(registerPayload, skipMapCardAllocation) {
            const { year: controlYear } = extractYearMonthFromControlDate(controlDateRef.current);
            if (Number.isInteger(controlYear) && String(form.year) !== String(controlYear)
                && client && client.confmsg) {
                client.confmsg(
                    '入力された年度は、当年度ではありません。よろしいですか？',
                    () => executeRegister(registerPayload, skipMapCardAllocation)
                );
                return;
            }

            executeRegister(registerPayload, skipMapCardAllocation);
        }

        function executeRegister(registerPayload, skipMapCardAllocation) {
            const param = {
                data: JSON.stringify(registerPayload.data)
            };
            if (registerPayload.image && registerPayload.image.file) {
                param.image = registerPayload.image.file;
            }

            const requestUrl = editMode ? 'tenkaimd/UpdateHinban' : 'tenkaimd/RegisterHinban';
            const errorTitle = editMode ? '品番の更新に失敗しました。' : '品番の登録に失敗しました。';

            client.fileupload(
                requestUrl,
                param,
                {
                    onSuccess: function (data) {
                        client.success();
                        client.wait(false);

                        if (props.onRegisterSuccess) {
                            // 登録された加工指示No／PS品番を親へ返し、仮カード取得に使用する。
                            props.onRegisterSuccess(data, {
                                skipMapCardAllocation,
                                registerData: registerPayload.data
                            });
                        }
                    },
                    onError: function (data) {
                        client.wait(false);
                        if (client.warnmsg) {
                            client.warnmsg(buildRegistrationWarningLines(
                                errorTitle,
                                data && data.errDescription
                            ));
                        }
                    }
                }
            );
        }
    }

    const fieldItemStyle = {
        minWidth: 0,
        padding: 8,
        borderRight: `1px solid ${COLORS.line}`,
        borderBottom: `1px solid ${COLORS.line}`
    };
    const matrixCellStyle = {
        borderRight: `1px solid ${COLORS.line}`,
        borderBottom: `1px solid ${COLORS.line}`,
        padding: 8,
        minWidth: 0
    };
    const matrixHeaderStyle = {
        ...matrixCellStyle,
        backgroundColor: COLORS.headBg,
        color: COLORS.headText,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };
    return (
        <Dialog
            className="md-tounyu-hinban-dialog"
            open={props.open}
            onClose={handleDialogClose}
            onDragEnter={handleImageDragEnter}
            maxWidth={false}
            PaperProps={{
                style: {
                    width: 'calc(100vw - 48px)',
                    maxWidth: 1000
                }
            }}
        >
            <style>
                {`
                .md-tounyu-hinban-dialog input[type="number"]::-webkit-inner-spin-button,
                .md-tounyu-hinban-dialog input[type="number"]::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .md-tounyu-hinban-dialog input[type="number"] {
                    -moz-appearance: textfield;
                }
                `}
            </style>
            <Box display="flex" alignItems="center" justifyContent="flex-end">
                <DialogActions style={{ paddingRight: 24 }}>
                    <Button
                        variant="contained"
                        onClick={handleRegister}
                        style={{ backgroundColor: COLORS.btn, color: COLORS.btnText }}
                    >
                        登録
                    </Button>
                    <Button
                        variant="contained"
                        onClick={props.onClose}
                        style={{ backgroundColor: COLORS.btn, color: COLORS.btnText }}
                    >
                        閉じる
                    </Button>
                </DialogActions>
            </Box>

            <DialogContent dividers={true} style={{ overflowY: 'auto', padding: 20 }}>
                <Box style={{ marginBottom: 16, overflow: 'hidden' }}>
                    <Box
                        minHeight={64}
                        px={2}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        bgcolor={COLORS.headBg}
                        color={COLORS.headText}
                        style={{
                            fontSize: 16,
                            fontWeight: 700
                        }}
                        textAlign="center"
                    >
                        <Box component="div" style={{ lineHeight: 1.6 }}>
                            {form.kariHinban || '未採番'}
                        </Box>
                        <Box component="div" style={{ lineHeight: 1.6 }}>
                            【{form.hinban || '未採番'}】
                        </Box>
                    </Box>

                    <Box p={2}>
                        <Box style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <FormField
                                        name="hinbanName"
                                        label="品番名"
                                        value={form.hinbanName}
                                        onChange={handleFormChange}
                                        onBlur={handleFormBlur}
                                        error={errors.hinbanName}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormField
                                        name="brand"
                                        label="ブランド"
                                        value={form.brand}
                                        onChange={handleFormChange}
                                        onBlur={handleFormBlur}
                                        onSearch={handleBrandSearchOpen}
                                        error={errors.brand}
                                        required={true}
                                        readOnly={true}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Box>

                <Box style={{
                    marginBottom: 16,
                    borderTop: `1px solid ${COLORS.line}`,
                    borderLeft: `1px solid ${COLORS.line}`,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))'
                }}>
                        <Box
                            minHeight={286}
                            display="flex"
                            flexDirection="column"
                            style={{
                                backgroundColor: 'rgb(240, 240, 240)',
                                borderRight: `1px solid ${COLORS.line}`,
                                borderBottom: `1px solid ${COLORS.line}`,
                                gridColumn: 'span 2',
                                gridRow: 'span 5'
                            }}
                        >
                            <Box flex={1} display="flex" alignItems="center" justifyContent="center" color="text.secondary">
                                {selectedImage ? (
                                    <img
                                        src={selectedImage.imageurl}
                                        alt={selectedImage.filename}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <Typography>絵型未登録</Typography>
                                )}
                            </Box>
                        </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="kikakuNo" label="企画No" value={form.kikakuNo} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.kikakuNo} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="kikakuName" label="企画名" value={form.kikakuName} onChange={handleFormChange} readOnly={true} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="mdTanto" label="MD担当者" value={form.mdTanto} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.mdTanto} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="season" label="シーズン" value={form.season} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.season} required={true} select={true} options={seasonOptions} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="year" label="年度" value={form.year} onChange={handleFormChange} onBlur={handleFormBlur} onSearch={handleYearSearchOpen} error={errors.year} required={true} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="salesSeason" label="販売シーズン" value={form.salesSeason} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.salesSeason} required={true} select={true} options={salesSeasonOptions} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="item" label="アイテム" value={form.item} onChange={handleFormChange} onSearch={handleItemSearchOpen} readOnly={true} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="category" label="カテゴリ" value={form.category} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.category} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="ecName" label="EC表示名" value={form.ecName} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.ecName} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="shopDate" label="店頭展開日" value={form.shopDate} onChange={handleFormChange} onBlur={handleFormBlur} onSearch={handleShopDateSearchOpen} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="rolloutMonth" label="展開月" value={form.rolloutMonth} onChange={handleFormChange} readOnly={true} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="rolloutWeek" label="展開週" value={form.rolloutWeek} onChange={handleFormChange} readOnly={true} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="patternNo" label="パターンNo" value={form.patternNo} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.patternNo} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="sizeExpansionCode" label="サイズ展開コード" value={form.sizeExpansionCode} onChange={handleFormChange} select={true} options={sizeExpansionOptions} />
                                </Box>
                                <Box style={fieldItemStyle}>
                                    <FormField name="sizePattern" label="サイズパターン" value={form.sizePattern} onChange={handleFormChange} readOnly={true} />
                                </Box>
                        <Box style={fieldItemStyle}>
                            <FormField name="provisionalPrice" label="仮上代" value={form.provisionalPrice} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.provisionalPrice} type="number" />
                        </Box>
                        <Box style={fieldItemStyle}>
                            <FormField name="latestPrice" label="最新上代" value={form.latestPrice} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.latestPrice} type="number" />
                        </Box>
                        <Box style={fieldItemStyle}>
                            <FormField name="provisionalCost" label="仮原価" value={form.provisionalCost} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.provisionalCost} type="number" />
                        </Box>
                        <Box style={fieldItemStyle}>
                            <FormField name="plannedCost" label="予定原価" value={form.plannedCost} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.plannedCost} type="number" />
                        </Box>
                        <Box style={fieldItemStyle}>
                            <FormField name="costRate" label="原価率" value={form.costRate} onChange={handleFormChange} readOnly={true} />
                        </Box>
                        <Box style={fieldItemStyle}>
                            <FormField name="supplierProductNo" label="先方品番" value={form.supplierProductNo} onChange={handleFormChange} onBlur={handleFormBlur} error={errors.supplierProductNo} inputMode="latin" autoCapitalize="off" spellCheck={false} />
                        </Box>
                        <Box style={fieldItemStyle}>
                            <FormField name="instructionNo" label="加工指示番号" value={form.instructionNo} onChange={handleFormChange} readOnly={true} />
                        </Box>
                        <Box style={fieldItemStyle}>
                            <FormField name="supplier" label="仕入先" value={form.supplier} onChange={handleFormChange} onSearch={handleSupplierSearchOpen} readOnly={true} />
                        </Box>
                        <Box style={fieldItemStyle}>
                            <FormField name="processor" label="加工先" value={form.processor} onChange={handleFormChange} onSearch={handleProcessorSearchOpen} readOnly={true} />
                        </Box>
                        <Box style={fieldItemStyle} aria-hidden={true} />
                </Box>

                <Paper variant="outlined" style={{ marginBottom: 16, overflow: 'auto', maxHeight: 430 }}>
                    <Box
                        style={{
                            display: 'grid',
                            width: '100%',
                            minWidth: matrixMinWidth,
                            gridTemplateColumns: `minmax(140px, 1.2fr) repeat(${displayedSizes.length}, minmax(110px, 1fr)) minmax(90px, 0.8fr)`
                        }}
                        borderTop={`1px solid ${COLORS.line}`}
                        borderLeft={`1px solid ${COLORS.line}`}
                    >
                        <Box style={{ ...matrixHeaderStyle, justifyContent: 'flex-start', fontSize: 16 }}>
                            カラー／サイズ
                        </Box>
                        {displayedSizes.map((size, sizeIndex) => (
                            <Box
                                key={`size-${sizeIndex}`}
                                style={{ ...matrixHeaderStyle, cursor: 'pointer' }}
                                onDoubleClick={handleSizeDoubleClick}
                            >
                                {size}
                            </Box>
                        ))}
                        <Box style={matrixHeaderStyle}>Total</Box>

                        {displayedColors.map((color, colorIndex) => (
                            <React.Fragment key={`color-${colorIndex}`}>
                                <Box
                                    style={{ ...matrixHeaderStyle, backgroundColor: COLORS.headBg, cursor: 'pointer' }}
                                    onDoubleClick={handleColorDoubleClick}
                                >
                                    {color}
                                </Box>
                                {displayedSizes.map((size, sizeIndex) => {
                                    const quantity = quantities[colorIndex] && quantities[colorIndex][sizeIndex]
                                        ? quantities[colorIndex][sizeIndex]
                                        : '';
                                    const quantityError = quantityErrors[colorIndex] && quantityErrors[colorIndex][sizeIndex]
                                        ? quantityErrors[colorIndex][sizeIndex]
                                        : '';
                                    const isQuantityEnabled = String(color || '').trim() !== ''
                                        && String(size || '').trim() !== '';

                                    return (
                                        <Box
                                            key={`quantity-${colorIndex}-${sizeIndex}`}
                                            style={{
                                                ...matrixCellStyle,
                                                backgroundColor: colorIndex % 2 === 0 ? '#fff' : COLORS.listEvenBg
                                            }}
                                        >
                                            <Tooltip
                                                title={quantityError}
                                                disableHoverListener={quantityError === ''}
                                            >
                                                <TextField
                                                    value={quantity}
                                                    onChange={(event) => handleQuantityChange(colorIndex, sizeIndex, event.target.value)}
                                                    onBlur={(event) => handleQuantityBlur(colorIndex, sizeIndex, event.target.value)}
                                                    type="number"
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth={true}
                                                    error={quantityError !== ''}
                                                    disabled={!isQuantityEnabled}
                                                />
                                            </Tooltip>
                                        </Box>
                                    );
                                })}
                                <Box style={matrixHeaderStyle}>
                                    {rowTotals[colorIndex]}
                                </Box>
                            </React.Fragment>
                        ))}

                        <Box style={matrixHeaderStyle}>Total</Box>
                        {displayedSizes.map((size, sizeIndex) => (
                            <Box key={`size-total-${sizeIndex}`} style={matrixHeaderStyle}>
                                {columnTotals[sizeIndex]}
                            </Box>
                        ))}
                        <Box style={matrixHeaderStyle}>{grandTotal}</Box>
                    </Box>
                </Paper>

                <Box style={{ marginBottom: 12 }}>
                    <FormField
                        name="productDescription"
                        label="商品説明"
                        value={form.productDescription}
                        onChange={handleFormChange}
                        onBlur={handleFormBlur}
                        error={errors.productDescription}
                        multiline={true}
                        rows={2}
                    />
                </Box>
                <Box>
                    <FormField
                        name="shopComment"
                        label="店舗向けコメント"
                        value={form.shopComment}
                        onChange={handleFormChange}
                        onBlur={handleFormBlur}
                        error={errors.shopComment}
                        multiline={true}
                        rows={2}
                    />
                </Box>
            </DialogContent>
            <DropZone
                open={isImageDragActive}
                onclose={handleImageDropClose}
                uploaded={handleImageUploaded}
                localOnly={true}
            />
        </Dialog>
    );
}
